import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Send, Calendar, Users, MapPin, Wallet, Mail, Phone, User } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Checkbox } from './ui/checkbox';
import { destinations, budgetRanges, companyInfo } from '../data/mock';
import { toast } from 'sonner';
import axios from 'axios';

// Use PHP endpoint for shared hosting, or backend API if available
const getApiEndpoint = () => {
  // For shared hosting: use relative path to PHP file
  if (window.location.hostname !== 'localhost') {
    return '/send-inquiry.php';
  }
  // For development: use backend API
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  return `${BACKEND_URL}/api/inquiries`;
};

export const ContactSection = () => {
  const { t } = useTranslation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    dates: '',
    guests: '',
    budget: '',
    message: '',
    destinations: [],
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      // Send form data to API endpoint
      const endpoint = getApiEndpoint();
      await axios.post(endpoint, formData);
      
      toast.success(t('contact.form.success'));
      setFormData({
        name: '',
        email: '',
        phone: '',
        dates: '',
        guests: '',
        budget: '',
        message: '',
        destinations: [],
      });
    } catch (error) {
      console.error('Failed to submit inquiry:', error);
      toast.error(t('contact.form.error'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDestinationToggle = (destId) => {
    setFormData(prev => ({
      ...prev,
      destinations: prev.destinations.includes(destId)
        ? prev.destinations.filter(id => id !== destId)
        : [...prev.destinations, destId]
    }));
  };

  return (
    <section className="py-24 bg-gradient-to-b from-white to-stone-50" id="contact">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl sm:text-5xl font-serif text-stone-800 mb-4">
            {t('contact.title')}
          </h2>
          <p className="text-lg text-stone-600 max-w-2xl mx-auto">
            {t('contact.subtitle')}
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <div className="bg-white rounded-3xl shadow-xl shadow-stone-900/5 overflow-hidden">
            <div className="grid lg:grid-cols-5">
              {/* Form Side */}
              <div className="lg:col-span-3 p-8 lg:p-12">
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Name & Email Row */}
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-stone-700 flex items-center gap-2">
                        <User className="w-4 h-4 text-amber-600" />
                        {t('contact.form.name')}
                      </label>
                      <Input
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        placeholder="John Smith"
                        required
                        className="border-stone-200 focus:border-amber-400 focus:ring-amber-400/20"
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-stone-700 flex items-center gap-2">
                        <Mail className="w-4 h-4 text-amber-600" />
                        {t('contact.form.email')}
                      </label>
                      <Input
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        placeholder="john@example.com"
                        required
                        className="border-stone-200 focus:border-amber-400 focus:ring-amber-400/20"
                      />
                    </div>
                  </div>

                  {/* Phone & Dates Row */}
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-stone-700 flex items-center gap-2">
                        <Phone className="w-4 h-4 text-amber-600" />
                        {t('contact.form.phone')}
                      </label>
                      <Input
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        placeholder="+1 234 567 8900"
                        className="border-stone-200 focus:border-amber-400 focus:ring-amber-400/20"
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-stone-700 flex items-center gap-2">
                        <Calendar className="w-4 h-4 text-amber-600" />
                        {t('contact.form.dates')}
                      </label>
                      <Input
                        value={formData.dates}
                        onChange={(e) => setFormData({ ...formData, dates: e.target.value })}
                        placeholder="e.g., June 2025"
                        className="border-stone-200 focus:border-amber-400 focus:ring-amber-400/20"
                      />
                    </div>
                  </div>

                  {/* Guests & Budget Row */}
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-stone-700 flex items-center gap-2">
                        <Users className="w-4 h-4 text-amber-600" />
                        {t('contact.form.guests')}
                      </label>
                      <Select
                        value={formData.guests}
                        onValueChange={(value) => setFormData({ ...formData, guests: value })}
                      >
                        <SelectTrigger className="border-stone-200 focus:border-amber-400 focus:ring-amber-400/20">
                          <SelectValue placeholder="Select..." />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="1">1 Guest</SelectItem>
                          <SelectItem value="2">2 Guests</SelectItem>
                          <SelectItem value="3-4">3-4 Guests</SelectItem>
                          <SelectItem value="5-8">5-8 Guests</SelectItem>
                          <SelectItem value="9+">9+ Guests</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-stone-700 flex items-center gap-2">
                        <Wallet className="w-4 h-4 text-amber-600" />
                        {t('contact.form.budget')}
                      </label>
                      <Select
                        value={formData.budget}
                        onValueChange={(value) => setFormData({ ...formData, budget: value })}
                      >
                        <SelectTrigger className="border-stone-200 focus:border-amber-400 focus:ring-amber-400/20">
                          <SelectValue placeholder="Select..." />
                        </SelectTrigger>
                        <SelectContent>
                          {budgetRanges.map((range) => (
                            <SelectItem key={range.value} value={range.value}>
                              {range.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* Destinations */}
                  <div className="space-y-3">
                    <label className="text-sm font-medium text-stone-700 flex items-center gap-2">
                      <MapPin className="w-4 h-4 text-amber-600" />
                      {t('contact.form.destinations')}
                    </label>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                      {destinations.slice(0, 6).map((dest) => (
                        <div
                          key={dest.id}
                          className="flex items-center space-x-2"
                        >
                          <Checkbox
                            id={dest.id}
                            checked={formData.destinations.includes(dest.id)}
                            onCheckedChange={() => handleDestinationToggle(dest.id)}
                            className="border-stone-300 data-[state=checked]:bg-amber-500 data-[state=checked]:border-amber-500"
                          />
                          <label
                            htmlFor={dest.id}
                            className="text-sm text-stone-600 cursor-pointer"
                          >
                            {dest.name}
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Message */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-stone-700">
                      {t('contact.form.message')}
                    </label>
                    <Textarea
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      placeholder="Share your travel dreams with us..."
                      rows={4}
                      className="border-stone-200 focus:border-amber-400 focus:ring-amber-400/20 resize-none"
                    />
                  </div>

                  {/* Submit Button */}
                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-amber-500 hover:bg-amber-600 text-white py-6 text-base font-medium shadow-lg shadow-amber-500/25 hover:shadow-xl hover:shadow-amber-500/30 transition-all duration-300"
                  >
                    {isSubmitting ? (
                      <div className="flex items-center gap-2">
                        <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        Sending...
                      </div>
                    ) : (
                      <>
                        {t('contact.form.submit')}
                        <Send className="ml-2 w-5 h-5" />
                      </>
                    )}
                  </Button>
                </form>
              </div>

              {/* Info Side */}
              <div className="lg:col-span-2 bg-stone-900 p-8 lg:p-12 text-white relative overflow-hidden">
                {/* Background Pattern */}
                <div className="absolute inset-0 opacity-10">
                  <div className="absolute inset-0" style={{
                    backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
                  }} />
                </div>

                <div className="relative z-10">
                  <h3 className="text-2xl font-serif mb-6">Contact Information</h3>
                  
                  <div className="space-y-6">
                    <div>
                      <div className="text-stone-400 text-sm mb-1">Email</div>
                      <a href={`mailto:${companyInfo.email}`} className="text-amber-400 hover:text-amber-300 transition-colors">
                        {companyInfo.email}
                      </a>
                    </div>
                    
                    <div>
                      <div className="text-stone-400 text-sm mb-1">Phone</div>
                      <div className="text-white">{companyInfo.phone}</div>
                    </div>
                    
                    <div>
                      <div className="text-stone-400 text-sm mb-1">Address</div>
                      <div className="text-white">{companyInfo.address}</div>
                    </div>
                  </div>

                  <div className="mt-12 pt-8 border-t border-stone-700">
                    <div className="text-stone-400 text-sm mb-4">Follow Us</div>
                    <div className="flex gap-4">
                      <a href="#" className="text-white hover:text-amber-400 transition-colors">
                        Instagram
                      </a>
                      <a href="#" className="text-white hover:text-amber-400 transition-colors">
                        Facebook
                      </a>
                      <a href="#" className="text-white hover:text-amber-400 transition-colors">
                        LinkedIn
                      </a>
                    </div>
                  </div>

                  {/* Decorative Image */}
                  <div className="mt-12 rounded-xl overflow-hidden">
                    <img
                      src="https://images.unsplash.com/photo-1656247203824-3d6f99461ba4"
                      alt="Bali Landscape"
                      className="w-full h-40 object-cover"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
